// shared-package/api-client.ts
import Kitsu from "kitsu";
import axios from "axios";
import qs from "qs";
const isUnauthorizedStatus = (status) => Number(status) === 401;
export const isUnauthorizedError = (error) => {
    if (isUnauthorizedStatus(error?.response?.status) || isUnauthorizedStatus(error?.status)) {
        return true;
    }
    const responseErrors = error?.response?.data?.errors;
    if (Array.isArray(responseErrors)) {
        return responseErrors.some((item) => isUnauthorizedStatus(item?.status));
    }
    if (Array.isArray(error?.errors)) {
        return error.errors.some((item) => isUnauthorizedStatus(item?.status));
    }
    return false;
};
/**
 * Screens reachable without a session. A 401 raised while the user sits on one
 * of them must never produce a "session expired" toast or a redirect - it would
 * throw the user off the password-reset link they just opened from their inbox.
 */
export const PUBLIC_ROUTE_PATHS = ["/", "/login", "/reset-password"];
export const isPublicRoutePath = (pathname) => PUBLIC_ROUTE_PATHS.includes(pathname.replace(/\/+$/, "") || "/");
export const isPublicRoute = () => {
    if (typeof window === "undefined") {
        return true;
    }
    return isPublicRoutePath(window.location.pathname);
};
export const getCurrentBrowserUrl = () => {
    if (typeof window === "undefined" || isPublicRoute()) {
        return undefined;
    }
    return window.location.href;
};
/**
 * Endpoints that authenticate the user themselves. A 401 coming back from these
 * means "wrong credentials" or "bad reset token" and belongs to the caller - it
 * must not be turned into a global logout.
 */
const AUTH_ENDPOINT_PATTERN = /\/(auth|auth\/reset-password|auth\/change-password|users\/logout|users\/impersonate)\/?$/;
export const isAuthEndpointUrl = (url) => {
    if (!url) {
        return false;
    }
    // `url` may be relative ("/auth") or absolute; only the path matters.
    const pathname = url.split("?")[0].split("#")[0];
    return AUTH_ENDPOINT_PATTERN.test(pathname);
};
/**
 * Collapses the burst of 401s a single expired session produces into one
 * `onUnauthorized` call.
 *
 * The client now lives for the whole lifetime of the app, so this cannot be a
 * plain boolean - it would swallow every later session expiry. It remembers
 * which token tripped it instead: further 401s carrying that same token, or
 * arriving after the token was already cleared, are deduplicated, while a brand
 * new session is always able to trip it again.
 */
export const createUnauthorizedLatch = (getAuthToken, onUnauthorized) => {
    let latched = false;
    let latchedToken = null;
    return (backToUrl) => {
        const currentToken = getAuthToken() ?? null;
        if (latched && (currentToken === null || currentToken === latchedToken)) {
            return;
        }
        latched = true;
        latchedToken = currentToken;
        onUnauthorized(backToUrl);
    };
};
export const createApiClient = (config) => {
    const handleUnauthorized = createUnauthorizedLatch(() => config.getAuthToken(), (backToUrl) => config.onUnauthorized(backToUrl));
    const createKitsu = () => new Kitsu({
        baseURL: config.apiUrl,
        query: "modern",
        axiosConfig: {
            paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "brackets" }),
        },
    });
    // Kitsu instance
    const kitsu = createKitsu();
    // Unauthenticated Kitsu instance for endpoints that are reachable from public
    // screens (e.g. the API version badge on the login page). It sends no
    // Authorization header and never triggers the global unauthorized handler, so
    // a leftover expired token cannot log the user out of a screen they are not
    // even logged into.
    const publicKitsu = createKitsu();
    // Common request interceptor
    const addAuthHeader = (requestConfig) => {
        const token = config.getAuthToken();
        if (token) {
            requestConfig.headers.Authorization = `Bearer ${token}`;
        }
        return requestConfig;
    };
    // Configure Kitsu instance
    kitsu.interceptors.request.use(addAuthHeader);
    kitsu.interceptors.response.use((response) => {
        return response;
    }, (error) => {
        if (isUnauthorizedError(error) &&
            !isAuthEndpointUrl(error?.config?.url || "")) {
            // Get the current URL from the browser when 401 occurs
            handleUnauthorized(getCurrentBrowserUrl());
        }
        return Promise.reject(error);
    });
    // Configure Axios instance
    const axiosInstance = axios.create({
        baseURL: config.apiUrl,
        paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "brackets" }),
    });
    axiosInstance.interceptors.request.use(addAuthHeader);
    axiosInstance.interceptors.response.use((response) => {
        return response;
    }, (error) => {
        if (isUnauthorizedError(error) &&
            !isAuthEndpointUrl(error?.config?.url || "")) {
            // Get the current URL from the browser when 401 occurs
            handleUnauthorized(getCurrentBrowserUrl());
        }
        return Promise.reject(error);
    });
    return {
        api: kitsu,
        publicApi: publicKitsu,
        axios: axiosInstance,
        getAuthToken: config.getAuthToken,
        getApiUrl: () => config.apiUrl,
    };
};
// Utility for direct Axios requests
export const createApiRequest = (client) => (config) => {
    return client.axios.request({
        ...config,
        headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            ...config.headers,
        },
    });
};
//# sourceMappingURL=api.js.map